import { useCallback, useRef, useState, useEffect } from 'react';

interface UsePullToRefreshProps {
  onRefresh: () => Promise<void>;
  threshold?: number;
}

interface PullToRefreshState {
  isPulling: boolean;
  isRefreshing: boolean;
  pullProgress: number;
}

export const usePullToRefresh = ({ 
  onRefresh, 
  threshold = 80 
}: UsePullToRefreshProps) => {
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const isPullingRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLElement | null>(null);
  
  const [state, setState] = useState<PullToRefreshState>({
    isPulling: false,
    isRefreshing: false,
    pullProgress: 0
  });

  const handleRefresh = useCallback(async () => {
    setState(prev => ({ ...prev, isRefreshing: true, isPulling: false, pullProgress: 0 }));
    
    try {
      await onRefresh();
    } finally {
      setState({ isPulling: false, isRefreshing: false, pullProgress: 0 });
    }
  }, [onRefresh]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const container = containerRef.current;
    if (!container) return;
    
    // Only enable pull-to-refresh when scrolled to top
    if (container.scrollTop <= 0) {
      startY.current = e.touches[0].clientY;
      isPullingRef.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPullingRef.current) return;
    
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) {
      isPullingRef.current = false;
      setState(prev => ({ ...prev, isPulling: false, pullProgress: 0 }));
      return;
    }

    currentY.current = e.touches[0].clientY;
    const pullDistance = Math.max(0, currentY.current - startY.current);
    
    if (pullDistance > 10) {
      e.preventDefault();
      const progress = Math.min(pullDistance / threshold, 1.5);
      setState(prev => ({ ...prev, isPulling: true, pullProgress: progress }));
    }
  }, [threshold]);

  const handleTouchEnd = useCallback(() => {
    if (!isPullingRef.current) return;
    
    const pullDistance = currentY.current - startY.current;
    
    if (pullDistance >= threshold && !state.isRefreshing) {
      handleRefresh();
    } else {
      setState(prev => ({ ...prev, isPulling: false, pullProgress: 0 }));
    }
    
    isPullingRef.current = false;
    startY.current = 0;
    currentY.current = 0;
  }, [threshold, state.isRefreshing, handleRefresh]);

  const setContainerRef = useCallback((element: HTMLElement | null) => {
    containerRef.current = element;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    ...state,
    setContainerRef
  };
};
